/**
 * Englische Texte.
 *
 * Vorbereitung fuer die spaetere Mehrsprachigkeit: Der Typ
 * `TranslationDictionary` erzwingt, dass jede in `de.ts` definierte
 * Zeichenkette auch hier vorhanden ist. Fehlende Schluessel fuehren zu einem
 * Kompilierfehler und koennen daher nicht unbemerkt in Produktion gelangen.
 */
import type { TranslationDictionary } from './de.js';

export const en: TranslationDictionary = {
  'app.title': 'AudIT – License Management',
  'app.productSuffix': 'License management',
  'app.subtitle': 'Central management and reporting for SaaS licenses',

  'nav.dashboard': 'Overview',
  'nav.countries': 'Country report',
  'nav.assignments': 'Assignments',
  'nav.people': 'People',
  'nav.orphans': 'Orphaned licences',
  'orphans.title': 'Orphaned licences',
  'orphans.description': 'Licences at other providers whose person is no longer active in the directory. A provider never learns that someone has left the company – the seat keeps running until somebody cancels it.',
  'orphans.count.one': '1 licence',
  'orphans.count.other': '{count} licences',
  'orphans.reason.DIRECTORY_DISABLED': 'account disabled',
  'orphans.reason.DIRECTORY_REMOVED': 'account removed',
  'orphans.reason.NOT_IN_DIRECTORY': 'no directory account',
  'orphans.reason.NO_ADDRESS': 'no address',
  'orphans.reasonHint.DIRECTORY_DISABLED': 'The directory still lists this person but has disabled the account. The clearest case: the licence can be cancelled.',
  'orphans.reasonHint.DIRECTORY_REMOVED': 'The directory no longer delivers this account – it was deleted. The licence can be cancelled.',
  'orphans.reasonHint.NOT_IN_DIRECTORY': 'None of this person’s addresses appear in the directory. That may be someone who left long ago – or an external person (consultancy, agency), or a domain spelled differently in the directory. Check, do not cancel.',
  'orphans.reasonHint.NO_ADDRESS': 'The provider reports no address for this seat. No statement about the person is possible; fix this at the source.',
  'orphans.actionable': 'clear-cut',
  'orphans.toCheck': 'to check',
  'orphans.kpi.actionable': 'Clearly orphaned',
  'orphans.kpi.actionableHint': 'The directory knows this person and no longer carries them – disabled or deleted.',
  'orphans.kpi.savings': 'Savings / month',
  'orphans.kpi.savingsHint': 'Clear-cut cases only, and only where a price is on record.',
  'orphans.kpi.toCheck': 'To check',
  'orphans.kpi.toCheckHint': 'No directory account found, or no address reported. External people look exactly the same – this needs a look.',
  'orphans.perYear': '{value} per year',
  'orphans.noDirectory': 'No directory source connected',
  'orphans.noDirectoryHint': 'This report compares every licence against the organisation’s directory (Microsoft 365). Without such a source there is no way to tell who still belongs to the company – and an empty list would not mean all clear.',
  'orphans.empty': 'No orphaned licences found.',
  'orphans.emptyHint': 'Every licence at the other providers belongs to a person who is active in the directory.',
  'orphans.excludeFree': 'Hide free licences',
  'orphans.directoryBasis': 'Compared against {providers} ({count} accounts).',
  'orphans.unpriced': '{count} without a price on record – they are missing from the savings.',
  'orphans.table.person': 'Person at the provider',
  'orphans.table.provider': 'Licence source',
  'orphans.table.license': 'Licence',
  'orphans.table.cost': 'Cost / month',
  'orphans.table.reason': 'Reason',
  'orphans.table.directory': 'In the directory',
  'orphans.table.directoryLastSeen': 'last reported {date}',
  'orphans.table.noDirectoryMatch': 'no match',
  'orphans.table.noAddress': 'no address reported',
  'dashboard.orphans': 'Orphaned licences',
  'dashboard.orphansHint': 'Licences at other providers for people who are disabled or deleted in the directory.',
  'dashboard.action.orphans': 'View orphaned licences',
  'nav.cart': 'Basket',
  'nav.myRequests': 'My requests',
  'nav.catalogRequests': 'Catalogue requests',
  'cart.title': 'Basket',
  'cart.description': 'What is in here goes to IT as a request. It becomes a helpdesk ticket – nothing is approved by sending it.',
  'cart.add': 'Add to basket',
  'cart.added': '“{name}” is in the basket.',
  'cart.inCart': 'In the basket',
  'cart.addNamed': 'Add “{name}” to the cart',
  'cart.inCartNamed': '“{name}” is in the cart',
  'cart.open': 'Open basket',
  'cart.empty': 'The basket is empty.',
  'cart.emptyHint': 'In the catalogue you can add hardware and software and request them together.',
  'cart.quantity': 'Quantity',
  'cart.remove': 'Remove',
  'cart.clear': 'Empty basket',
  'cart.lineNote': 'Note on this item',
  'cart.orderFieldFallback': 'Additional detail',
  'cart.orderFieldMissing': 'Please fill in – IT cannot order without this detail.',
  'cart.orderFieldMissingSummary': 'At least one item is missing a required detail. Please complete the highlighted fields.',
  'cart.note': 'Note for IT',
  'cart.notePlaceholder': 'What is it needed for? By when? Which cost centre?',
  'cart.submit': 'Send request',
  'cart.submitting': 'Sending …',
  'cart.count.one': '1 item',
  'cart.count.other': '{count} items',
  'cart.localHint': 'The basket lives in this browser. It does not follow you to another device and disappears when you clear your browser data.',
  'cart.backToCatalog': 'Back to the catalogue',
  'softwareRequest.title': 'Request new software',
  'softwareRequest.description': 'Something the catalogue does not list yet. The more detail, the fewer questions – only name and purpose are required.',
  'softwareRequest.privacyNoticeTitle': 'A data protection pre-check runs before the ticket.',
  'softwareRequest.privacyNotice': 'AudIT searches publicly available information about the vendor and has it pre-sorted automatically. The result is a draft for the data protection officers – not an approval and not a decision. Only details about the software are sent to the check, nothing about you.',
  'softwareRequest.name': 'Software name',
  'softwareRequest.vendor': 'Vendor',
  'softwareRequest.vendorUrl': 'Vendor website',
  'softwareRequest.vendorUrlHint': 'Helps the pre-check find the right product – many names exist more than once.',
  'softwareRequest.purpose': 'What is it needed for?',
  'softwareRequest.purposeHint': 'Which task should it solve, and who will work with it? The detail asked about most often.',
  'softwareRequest.alternatives': 'Alternatives considered',
  'softwareRequest.alternativesHint': 'What did you look at, and why is the existing tooling not enough?',
  'softwareRequest.userCount': 'How many people?',
  'softwareRequest.neededBy': 'Needed by',
  'softwareRequest.cost': 'Estimated cost',
  'softwareRequest.costHint': 'Per month, if known. A rough estimate beats nothing.',
  'softwareRequest.currency': 'Currency',
  'softwareRequest.dataHeading': 'Data',
  'softwareRequest.hostingHeading': 'Operation',
  'softwareRequest.hostingHint': 'Few people know these three – “I don’t know” is perfectly fine. The pre-check tries to find out the rest and says what it could and could not confirm.',
  'softwareRequest.hostingModel': 'Where does the software run?',
  'softwareRequest.hostingModel.UNKNOWN': 'I don’t know',
  'softwareRequest.hostingModel.SAAS': 'At the vendor (cloud/SaaS)',
  'softwareRequest.hostingModel.ON_PREMISES': 'In our own data centre',
  'softwareRequest.hostingModel.HYBRID': 'Mixed',
  'softwareRequest.hostingRegion': 'Which region holds the data?',
  'softwareRequest.hostingRegionHint': 'The key fact for data protection: outside the EU, extra requirements apply.',
  'softwareRequest.hostingRegion.UNKNOWN': 'I don’t know',
  'softwareRequest.hostingRegion.GERMANY': 'Germany',
  'softwareRequest.hostingRegion.EU': 'EU/EEA',
  'softwareRequest.hostingRegion.USA': 'USA',
  'softwareRequest.hostingRegion.OTHER': 'Elsewhere or several regions',
  'softwareRequest.ssoEntra': 'Can people sign in with their company account?',
  'softwareRequest.ssoEntraHint': 'Microsoft Entra ID – the same sign-in as here. Saves a separate password, and one more off-boarding step.',
  'softwareRequest.sso.UNKNOWN': 'I don’t know',
  'softwareRequest.sso.YES': 'Yes',
  'softwareRequest.sso.NO': 'No',
  'softwareRequest.dataHint': 'These answers decide how much work the review is. “I don’t know” is a valid answer – guessing helps nobody.',
  'softwareRequest.personalData': 'Does it process personal data?',
  'softwareRequest.personalData.YES': 'Yes',
  'softwareRequest.personalData.NO': 'No',
  'softwareRequest.personalData.UNKNOWN': 'I don’t know',
  'softwareRequest.dataCategories': 'Which data?',
  'softwareRequest.dataCategoriesHint': 'For example: name, work email, phone number, customer records.',
  'softwareRequest.dataSubjects': 'About whom?',
  'softwareRequest.dataSubjectsHint': 'For example: employees, customers, suppliers, applicants.',
  'softwareRequest.note': 'Anything else',
  'softwareRequest.submit': 'Submit request',
  'softwareRequest.sending': 'Checking and sending …',
  'softwareRequest.submitHint': 'The pre-check takes a moment. Submitting is not an approval.',
  'softwareRequest.noPermission': 'You do not have permission to submit requests. Please contact IT.',
  'softwareRequest.navNew': 'Request new software',
  'userRequest.navNew': 'User request',
  'todos.label': 'Open tasks',
  'todos.labelCount': '{count} open tasks',
  'todos.none': 'Nothing to do.',
  'todos.approvals.one': 'One approval waiting for you',
  'todos.approvals.other': '{count} approvals waiting for you',
  'todos.failedRequests.one': 'One request without ticket – resubmit',
  'todos.failedRequests.other': '{count} requests without ticket – resubmit',
  'userRequest.title': 'User request',
  'userRequest.description': 'For a person joining: accounts, permissions and handover. The request becomes a ticket for IT.',
  'userRequest.personSearch': 'Type a name or e-mail address …',
  'userRequest.choose': 'Please choose',
  'userRequest.required': 'Please fill in.',
  'userRequest.missingSummary': '{count} required answers are still missing. The fields are marked.',
  'userRequest.submit': 'Submit request',
  'userRequest.submitHint': 'The request is saved and sent to IT as a ticket.',
  'userRequest.sentTitle': 'Request {number} for {name} has been received.',
  'userRequest.another': 'Submit another request',
  'userRequest.myHeading': 'User requests',
  'userRequest.firstWorkday': 'first working day {date}',
  'userRequest.showAnswers': 'Show details',
  'wizard.overview': 'Your request',
  'wizard.stepOf': 'Step {current} of {total}',
  'wizard.back': 'Back',
  'wizard.next': 'Next',
  'wizard.review': 'Review & submit',
  'wizard.edit': 'Change',
  'wizard.here': 'You are here',
  'wizard.done': 'Complete',
  'wizard.empty': 'not specified',
  'wizard.open.one': 'One required answer missing',
  'wizard.open.other': '{count} required answers missing',
  'wizard.blocked.one': 'One required answer on this page is still missing. The field is marked.',
  'wizard.blocked.other': '{count} required answers on this page are still missing. The fields are marked.',
  'userRequest.reviewHint': 'Everything at a glance – this is what goes into the ticket. Use “Change” to go back to a page.',
  'softwareRequest.step.software': 'Software',
  'softwareRequest.step.softwareHint': 'Which software is it? The name is enough – vendor and website help the pre-check find the right one.',
  'softwareRequest.step.need': 'Purpose & need',
  'softwareRequest.step.needHint': 'What for, for whom and from when. The purpose is what IT asks about most often.',
  'softwareRequest.reviewHint': 'Everything at a glance. Use “Change” to go back to a page.',
  'softwareRequest.myHeading': 'Requests for new software',
  'softwareRequest.assessment': 'Data protection pre-check',
  'softwareRequest.assessmentDraft': 'Machine-generated draft – not an approval, not legal advice.',
  'softwareRequest.assessmentSources': 'Sources',
  'softwareRequest.assessmentMissing': 'No pre-check available.',
  'softwareRequest.assessmentRunning': 'The pre-check is running. It takes a minute or two; the ticket follows after that.',
  'softwareRequest.sentQueued': 'The pre-check is now running in the background – it takes a minute or two. The ticket follows, and you will find it under “My requests”.',
  'softwareRequest.assessmentRetry': 'Run the pre-check again',
  'softwareRequest.risk.LOW': 'low risk',
  'softwareRequest.risk.MEDIUM': 'medium risk',
  'softwareRequest.risk.HIGH': 'high risk',
  'softwareRequest.risk.UNKNOWN': 'cannot be assessed',
  'softwareRequest.catalogHint': 'Not finding what you need?',
  'request.sentTitle': 'Request {number} has been received',
  'request.sentWithTicket': 'It became ticket {ticket}. Everything else runs through that ticket.',
  'request.sentWithoutTicket': 'Your request has been saved. A ticket could not be created yet – IT can see the request all the same and will take care of it.',
  'request.openTicket': 'Open ticket',
  'request.toMyRequests': 'To my requests',
  'requests.title': 'My requests',
  'requests.description': 'What you have requested through the catalogue – and what became of it.',
  'requests.empty': 'You have not requested anything yet.',
  'requests.emptyHint': 'In the catalogue you can add hardware and software to the basket and request them.',
  'requests.number': 'Request {number}',
  'requests.status.PENDING': 'in progress',
  'requests.status.SUBMITTED': 'ticket created',
  'requests.status.FAILED': 'no ticket yet',
  'requests.status.CANCELLED': 'withdrawn',
  'requests.statusHint.PENDING': 'The request is saved; the ticket is about to be created.',
  'requests.statusHint.SUBMITTED': 'Everything else runs through the ticket.',
  'requests.statusHint.FAILED': 'The request is saved, but no ticket could be created yet. IT can see it all the same.',
  'requests.statusHint.CANCELLED': 'You withdrew this request.',
  'requests.status.AWAITING_APPROVAL': 'awaiting approval',
  'requests.status.REJECTED': 'rejected',
  'requests.statusHint.AWAITING_APPROVAL': 'The order is with the approver. A ticket is only raised once they approve it.',
  'requests.statusHint.REJECTED': 'The order was rejected. No ticket was raised.',
  'requests.approver': 'Approved by',
  'approvals.title': 'Approvals',
  'approvals.description': 'Orders waiting for your approval. A ticket reaches IT only once you approve.',
  'approvals.empty': 'Nothing to decide.',
  'approvals.emptyHint': 'Orders appear here when you are named as the approver.',
  'approvals.approve': 'Approve',
  'approvals.reject': 'Reject',
  'approvals.note': 'Note (please give a reason when rejecting)',
  'approvals.approved': 'Approved. Ticket raised: {ticket}',
  'approvals.approvedNoTicket': 'Approved. The ticket could not be raised yet – IT can still see the order.',
  'approvals.rejected': 'Rejected. No ticket will be raised.',
  'approvals.requestedBy': 'Requested by',
  'cart.approver': 'Approved by',
  'cart.approverHint': 'Your line manager. Without their approval no ticket is raised – they are notified when you submit.',
  'cart.approverSearch': 'Type a name or email …',
  'cart.approverSuggested': 'Suggested from the directory',
  'cart.approverMissing': 'Please choose who approves this order.',
  'cart.approverNoResults': 'No one found.',
  'cart.noApprovalNeeded': 'You can order without approval – this request goes straight to IT.',
  'nav.approvals': 'Approvals',
  'request.sentAwaitingApproval': 'It is now with {approver} for approval. A ticket reaches IT only after that.',
  'requests.cancel': 'Withdraw',
  'requests.cancelled': 'The request has been withdrawn.',
  'requests.ticket': 'Ticket',
  'requests.table.created': 'Requested on',
  'requests.table.items': 'Items',
  'requests.table.requester': 'Person',
  'requests.table.status': 'State',
  'requests.admin.title': 'Catalogue requests',
  'requests.admin.description': 'All requests from the catalogue. This is also where a request shows up that has not become a ticket yet – those rows need a hand.',
  'requests.admin.empty': 'There are no requests yet.',
  'requests.admin.emptyHint': 'As soon as someone sends a basket from the catalogue, the request appears here.',
  'requests.admin.retry': 'Create ticket again',
  'requests.admin.retried': 'Ticket {ticket} was created.',
  'requests.admin.assessQueued': 'The privacy pre-check is running in the background and takes about a minute. If the ticket is still missing, it is created afterwards.',
  'requests.admin.retryFailed': 'It failed again: {reason}',
  'requests.admin.openFailures': '{count} requests without a ticket',
  'requests.admin.openFailuresHint': 'These requests are saved but have not reached the helpdesk yet.',
  'requests.admin.attempts': '{count} attempts',
  'requests.admin.filterStatus': 'State',
  'nav.sync': 'Synchronisation',
  'nav.admin': 'Administration',
  'nav.audit': 'Audit log',

  'auth.signIn': 'Sign in',
  'auth.signOut': 'Sign out',
  'auth.signInPrompt': 'Please sign in with your work account.',
  'auth.vendorNote': 'An AI-assisted application by Krannich Group',
  'auth.signInHint': 'Only work or school accounts of this tenant are permitted.',
  'auth.signingIn': 'Signing in …',
  'auth.noRole': 'No role has been assigned to your account in this application.',
  'auth.noRoleHint': 'Please contact IT administration to have a role assigned.',
  'auth.sessionExpired': 'Your session has expired. Please sign in again.',

  'common.loading': 'Loading data …',
  'common.error': 'Error',
  'common.retry': 'Try again',
  'common.refresh': 'Refresh',
  'common.export': 'Export',
  'common.exportCsv': 'Export as CSV',
  'common.search': 'Search',
  'common.reportIssuePrefix': 'If something looks wrong, please report it via the',
  'common.ticketSystem': 'ticket system',
  'assets.field.assignedAt': 'Issued on',
  'assets.admin.assignedAtHint': 'Empty while neither the source system nor AudIT knows the date.',
  'filter.placeholder': 'Filter …',
  'filter.forColumn': 'Filter column {column}',
  'filter.clearAll': 'Reset all column filters',
  'filter.noMatch': 'No entry matches the column filters.',
  'filter.matches': '{count} of {total} after column filters',
  'filter.truncated': 'Only the first {count} rows are filtered – use the CSV export for the full set.',
  'common.filter': 'Filter',
  'common.all': 'All',
  'common.none': 'None',
  'common.unknown': 'Unknown',
  'common.yes': 'Yes',
  'common.no': 'No',
  'common.save': 'Save',
  'common.cancel': 'Cancel',
  'common.delete': 'Delete',
  'common.edit': 'Edit',
  'common.create': 'Create',
  'common.close': 'Close',
  'common.noData': 'No data available yet.',
  'common.noDataHint': 'Start a synchronisation to load license data.',
  'common.total': 'Total',
  'common.rowsShown': '{count} of {total} entries',
  'common.page': 'Page',
  'common.next': 'Next',
  'common.previous': 'Previous',
  'common.lastUpdate': 'As of',
  'common.never': 'Never',
  'common.accessDenied': 'Access denied',
  'common.accessDeniedHint': 'Your role does not permit access to this area.',
  'common.notFound': 'Page not found',

  'dashboard.title': 'License overview',
  'dashboard.totalUnits': 'Total licenses',
  'dashboard.totalUnitsHint': 'Purchased and active units',
  'dashboard.assignedUnits': 'Assigned',
  'dashboard.assignedUnitsHint': 'Currently assigned units',
  'dashboard.availableUnits': 'Available',
  'dashboard.availableUnitsHint': 'Unassigned units',
  'dashboard.unusedUnits': 'Unused',
  'dashboard.unusedUnitsHint': 'Assigned to disabled accounts',
  'dashboard.utilization': 'Utilisation',
  'dashboard.byProduct': 'Licenses by product',
  'dashboard.distribution': 'Assigned vs. available',
  'dashboard.trend': 'Trend',
  'dashboard.trendDays': 'Period',
  'dashboard.table.product': 'Product',
  'dashboard.table.source': 'Source',
  'dashboard.table.total': 'Total',
  'dashboard.table.assigned': 'Assigned',
  'dashboard.table.available': 'Available',
  'dashboard.table.disabled': 'Disabled accounts',
  'dashboard.table.inactive': 'Inactive accounts',
  'dashboard.table.utilization': 'Utilisation',

  'countries.title': 'Country report',
  'countries.description': 'Assigned licenses per country, derived from the usage location of the accounts.',
  'countries.table.country': 'Country',
  'countries.table.code': 'Code',
  'countries.table.assigned': 'Assigned',
  'countries.table.enabled': 'Active accounts',
  'countries.table.disabled': 'Disabled accounts',
  'countries.table.products': 'Products',
  'countries.chartTitle': 'Assigned licenses by country',
  'countries.filterProduct': 'Product',
  'countries.unknownHint': 'Accounts without a usage location are listed under “Unknown”.',

  'assignments.title': 'License assignments',
  'assignments.description': 'Detailed view of all currently assigned licenses.',
  'assignments.searchPlaceholder': 'Name, sign-in name, e-mail or department',
  'assignments.statusActive': 'Active accounts only',
  'assignments.statusDisabled': 'Disabled accounts only',
  'assignments.resetFilters': 'Reset filters',
  'assignments.filteredByName': 'via the name field',
  'assignments.groupByPerson': 'One row per person',
  'assignments.table.name': 'Name',
  'assignments.table.upn': 'Sign-in name',
  'assignments.productFilterPlaceholder': 'Filter … (quotes = exact)',
  'assignments.productFilterHint': 'Without quotes: contains. In quotes: exactly this name – e.g. "Microsoft 365 Copilot" without the free Copilot Chat.',
  'assignments.table.product': 'Product',
  'assignments.table.country': 'Country',
  'assignments.table.department': 'Department',
  'assignments.table.enabled': 'Account active',
  'assignments.table.source': 'Assignment',
  'assignments.table.lastSignIn': 'Last sign-in',
  'assignments.source.direct': 'Direct',
  'assignments.source.group': 'Group',
  'assignments.source.unknown': 'Unknown',

  'sync.title': 'Synchronisation',
  'sync.description': 'License data is refreshed automatically every day and can be triggered manually at any time.',
  'sync.trigger': 'Synchronise now',
  'sync.triggering': 'Starting synchronisation …',
  'sync.started': 'Synchronisation has started and continues in the background.',
  'sync.running': 'Synchronisation running',
  'sync.runningHint': 'You may leave this page; the process continues on the server.',
  'sync.schedule': 'Schedule',
  'sync.lastRun': 'Last run',
  'sync.history': 'History',
  'sync.table.started': 'Started',
  'sync.table.duration': 'Duration',
  'sync.table.trigger': 'Trigger',
  'sync.table.status': 'Status',
  'sync.table.source': 'Source',
  'sync.table.skus': 'Products',
  'sync.table.accounts': 'Accounts',
  'sync.table.assignments': 'Assignments',
  'sync.table.triggeredBy': 'Triggered by',
  'sync.trigger.SCHEDULED': 'Schedule',
  'sync.trigger.MANUAL': 'Manual',
  'sync.trigger.BACKFILL': 'Backfill',
  'sync.trigger.INGEST': 'Pushed',
  'sync.status.RUNNING': 'Running',
  'sync.status.SUCCEEDED': 'Succeeded',
  'sync.status.FAILED': 'Failed',
  'sync.status.PARTIAL': 'Completed with warnings',
  'sync.status.CANCELLED': 'Cancelled',
  'sync.warnings': 'Warnings',
  'sync.log': 'Log',
  'sync.logHint': 'Stages of the selected run. While a sync is in progress the list refreshes automatically.',
  'sync.logEmpty': 'No stages recorded for this run yet.',
  'sync.logSelect': 'Select a run in the history to see its log.',
  'sync.logOf': 'Run from {time}',
  'sync.elapsed': 'Running for',
  'sync.level.INFO': 'Info',
  'sync.level.WARN': 'Warning',
  'sync.level.ERROR': 'Error',

  'admin.title': 'Administration',
  'admin.sources': 'License sources',
  'admin.sourcesDescription': 'Configured SaaS providers from which license data is read.',
  'admin.setupSteps': 'Setup',
  'admin.setupDocs': 'Open the detailed guide',
  'admin.availableConnectors': 'Available connectors',
  'admin.connectorsDescription': 'Every connector this system knows, with setup steps and required permissions. A licence source is created from one of them under “Licence sources”.',
  'admin.addSource': 'Add license source',
  'admin.editSource': 'Edit license source',
  'admin.testConnection': 'Test connection',
  'admin.testSuccess': 'Connection successful.',
  'admin.testFailed': 'Connection failed.',
  'admin.deleteConfirmTitle': 'Delete license source?',
  'admin.deleteConfirmBody':
    'This permanently deletes all stored license states and the complete history of this source.',
  'admin.field.connector': 'Connector',
  'admin.field.slug': 'Technical key',
  'admin.field.slugHint': 'Lower-case letters, digits and hyphens. Cannot be changed after creation.',
  'admin.field.displayName': 'Display name',
  'admin.field.description': 'Description',
  'admin.field.enabled': 'Active',
  'admin.table.name': 'Name',
  'admin.table.connector': 'Connector',
  'admin.table.status': 'Status',
  'admin.table.lastSync': 'Last synchronisation',
  'admin.table.actions': 'Actions',
  'admin.connector.available': 'Available',
  'admin.connector.preview': 'Preview',
  'admin.connector.planned': 'Planned',
  'admin.requiredPermissions': 'Required permissions',
  'admin.secretHint':
    'This configuration is visible to everyone with read access. Do not enter a secret here — use a reference in the format “env:<NAME>” to an application setting. For sources of type “Push”, manage credentials in n8n instead.',

  'audit.title': 'Audit log',
  'audit.description': 'Record of all administrative actions and synchronisations.',
  'audit.table.time': 'Timestamp',
  'audit.table.actor': 'User',
  'audit.table.action': 'Action',
  'audit.table.target': 'Object',
  'audit.table.outcome': 'Outcome',
  'audit.table.details': 'Details',
  'audit.outcome.SUCCESS': 'Succeeded',
  'audit.outcome.FAILURE': 'Failed',
  'audit.outcome.DENIED': 'Denied',
  'audit.filterAction': 'Action',
  'audit.filterFrom': 'From',
  'audit.filterTo': 'To',

  'dashboard.includeDisabled': 'Include disabled accounts',
  'dashboard.excludedDisabled': '{count} units on disabled accounts excluded – Microsoft keeps charging for them while the license stays assigned.',
  'dashboard.includeFree': 'Include free licenses',
  'dashboard.hiddenFree': '{count} free products hidden – use the switch to include them',
  'dashboard.costAssigned': 'Cost assigned / month',
  'dashboard.costAssignedHint': 'Assigned units × monthly price',
  'dashboard.costAvailable': 'Cost unassigned / month',
  'dashboard.costAvailableHint': 'Paid for, but not assigned',
  'dashboard.costUnused': 'Cost disabled accounts / month',
  'dashboard.costUnusedHint': 'Assigned to disabled accounts – savings potential',
  'dashboard.unpriced': '{count} paid products without a price – cost totals are incomplete.',
  'dashboard.mixedCurrencies': 'Prices in different currencies – totals are not comparable.',
  'dashboard.noPrices': 'No prices yet. Maintain prices under Administration → Products and prices.',
  'dashboard.table.unitCost': 'Price/month',
  'dashboard.table.cost': 'Cost/month',
  'dashboard.table.free': 'free',
  'dashboard.table.noPrice': 'no price',

  'countries.table.cost': 'Cost/month',
  'countries.unpricedShort': '{count} without price',

  'admin.prices': 'Products and prices',
  'admin.pricesDescription': 'Monthly price per unit for cost controlling. Free products do not count towards costs and can be hidden in reports.',
  'admin.prices.netDisclaimer': 'Enter all prices net – excluding VAT, as shown as the net amount on the invoice. Cost reports are therefore net figures as well.',
  'admin.prices.hint': 'Enter annual contracts as their monthly equivalent. Empty means "no price" – costs are then unknown, not zero.',
  'admin.prices.onlyUnpriced': 'Only without price',
  'admin.prices.showRetired': 'Show retired',
  'admin.prices.saved': 'Price saved.',
  'admin.prices.table.product': 'Product',
  'admin.prices.table.source': 'Source',
  'admin.prices.table.total': 'Purchased',
  'admin.prices.table.assigned': 'Assigned',
  'admin.prices.table.free': 'Free',
  'admin.prices.table.unitCost': 'Price per unit / month',
  'admin.prices.table.currency': 'Currency',
  'admin.prices.table.updated': 'Last changed',
  'admin.prices.retired': 'retired',
  'admin.prices.emptyProvider': 'No run yet for this licence source. Products appear after the first synchronisation; manually maintained sources are taken over immediately on save.',
  'admin.checkItems': 'Check items',
  'admin.savedAndSynced': 'Licence source saved and synchronised – the items are listed under “Products and prices”.',
  'admin.savedSyncFailed': 'Licence source saved, but the run failed: {error}',
  'category.productivity': 'Productivity',
  'category.security': 'Security',
  'category.communication': 'Communication',
  'category.infrastructure': 'Infrastructure',
  'category.erp': 'ERP / Finance',
  'category.data': 'Data & Analytics',
  'category.ai': 'AI/automation',
  'category.development': 'Development',
  'category.creative': 'Creative / Design',
  'category.other': 'Other',
  'category.none': 'no category',
  'admin.prices.table.category': 'Category',
  'admin.manual.category': 'Category',
  'admin.manual.categoryHint': 'For the cost view by category, e.g. Security for CrowdStrike.',
  'dashboard.costByCategory': 'Cost by category',
  'dashboard.bySource': 'By licence source',
  'dashboard.source.hint': '{assigned} assigned',
  'dashboard.source.hintCost': '{assigned} assigned · {cost} / month',
  'dashboard.action.source': 'Show products of this licence source',
  'dashboard.action.allProducts': 'Show all products in the table below',
  'dashboard.action.assignments': 'Go to assignments of active accounts',
  'dashboard.action.available': 'Show products with unassigned units',
  'dashboard.action.disabled': 'Show assignments on disabled accounts',
  'dashboard.action.lowUtilization': 'Show products below 80 % utilisation',
  'dashboard.action.people': 'Show cost per person',
  'dashboard.action.shared': 'Show shared-cost products',
  'dashboard.action.countries': 'Show cost per country with allocation',
  'dashboard.action.category': 'Show products in this category',
  'dashboard.action.product': 'Show assignments of this product',
  'dashboard.action.showUnpriced': 'Show products without a price',
  'dashboard.focus.available': 'with unassigned units',
  'dashboard.focus.lowUtilization': 'below 80 % utilisation',
  'dashboard.focus.shared': 'shared cost',
  'dashboard.focus.unpriced': 'without price',
  'dashboard.focus.empty': 'No product matches this filter.',
  'dashboard.focus.reset': 'Remove filter “{filter}”',
  'dashboard.table.category': 'Category',
  'dashboard.table.products': 'Products',
  'dashboard.table.share': 'Share',
  'admin.members.pasteLabel': 'Paste list',
  'admin.members.pasteHint': 'In claude.ai, open “Organization settings → Members”, select the list, copy it and paste it here. Name and address are split automatically; lines without an address are skipped.',
  'admin.members.pastePlaceholder': 'Alexis Ladas  a.ladas@example.com\nBea Beispiel  b.beispiel@example.com',
  'admin.members.pasteApply': 'Apply',
  'admin.members.importFile': 'Import CSV file',
  'admin.members.importHint': 'The import replaces the list – an export is the complete state.',
  'admin.members.importedAt': 'Last imported: {date}',
  'admin.members.importResult': '“{file}”: {count} people imported, list replaced (before {before}). {skipped} rows without address, {removed} removed accounts skipped. Save to apply.',
  'admin.members.importEmpty': '“{file}” contains no recognisable e-mail address. The list is unchanged.',
  'admin.members.importFailed': '“{file}” could not be read. The list is unchanged.',
  'admin.members.pasteResult': '{added} new, {known} already present, {skipped} lines without an address skipped.',
  'admin.members.tableLabel': 'Members',
  'admin.members.name': 'Name',
  'admin.members.namePlaceholder': 'optional',
  'admin.members.email': 'Email address',
  'admin.members.duplicate': 'Address appears more than once',
  'admin.members.duplicatesHint': 'Duplicate addresses are rejected on save.',
  'admin.members.empty': 'No members yet. Paste a list above or add rows one by one.',
  'admin.members.add': 'Add row',
  'admin.members.count': '{count} people',
  'admin.seatGroups.tableLabel': 'Seat groups',
  'admin.seatGroups.groupId': 'Entra group object ID',
  'admin.seatGroups.name': 'Product name',
  'admin.seatGroups.namePlaceholder': 'empty = “Claude – group name”',
  'admin.seatGroups.seats': 'Purchased seats',
  'admin.seatGroups.seatsPlaceholder': 'empty = user count',
  'admin.seatGroups.invalidGuid': 'Not a GUID',
  'admin.seatGroups.duplicate': 'Group listed more than once',
  'admin.seatGroups.add': 'Add group',
  'admin.seatGroups.empty': 'No group yet. Add one Entra group per seat tier (e.g. standard, premium).',
  'admin.seatGroups.orderHint': 'Order = precedence: a person in several groups counts only in the topmost one. Put the pricier tier first.',
  'admin.seatGroups.moveUp': 'Move up',
  'admin.seatGroups.moveDown': 'Move down',
  'admin.seatGroups.count': '{count} groups',
  'admin.manual.title': 'Manually maintained items',
  'admin.manual.name': 'Contract / licence',
  'admin.manual.namePlaceholder': 'e.g. Sage (group contract)',
  'admin.manual.units': 'Quantity',
  'admin.manual.annualCost': 'Annual amount (net)',
  'admin.manual.currency': 'Currency',
  'admin.manual.countries': 'Allocate to',
  'admin.manual.allCountries': 'all countries',
  'admin.manual.addCountry': 'Country',
  'admin.manual.note': 'Note',
  'admin.manual.add': 'Add item',
  'admin.manual.empty': 'No items yet. Each row becomes a product with a fixed quantity on the next run.',
  'admin.manual.hint': 'Annual amount for all units, net. It seeds the monthly price per unit; changes under “Products and prices” win permanently.',
  'admin.manual.monthlyHint': '= {amount} per unit and month',
  'admin.manual.newItem': 'Item {index}',
  'admin.manual.idHint': 'Technical id: {id} – fixed once saved.',
  'admin.manual.countriesHint': 'Empty = all countries. If a country is missing from the list, add its two-letter code.',
  'admin.prices.table.countries': 'Allocate to',
  'admin.prices.countries.all': 'all countries',
  'admin.prices.countriesHint': 'Countries this product’s shared cost is allocated to. Empty = all countries. For a manually maintained licence source the source configuration applies.',
  'admin.prices.table.shared': 'Shared cost',
  'admin.prices.sharedHint': 'Shared cost: not assignable (e.g. Dataverse capacity, server licences). The whole quantity counts as consumed; the cost is allocated to countries in the country report using an allocation key.',
  'dashboard.costShared': 'Shared cost / month',
  'dashboard.costSharedHint': 'Non-assignable products, quantity × price – allocated in the country report',
  'dashboard.costTotal': 'Total cost / month',
  'dashboard.costTotalHint': 'Assigned plus shared cost',
  'dashboard.sharedBadge': 'shared cost',
  'dashboard.sharedSkus': '{count} products are shared cost: fully assigned per product, but not part of the unit counts above – their cost is shown as “Shared cost” and allocated in the country report.',
  'countries.table.heads': 'People',
  'countries.table.directCost': 'Direct cost/month',
  'countries.table.allocated': 'Allocation/month',
  'countries.table.total': 'Total/month',
  'countries.allocationKey': 'Allocate by',
  'countries.allocationKey.heads': 'people with licences',
  'countries.allocationKey.directCost': 'direct cost',
  'countries.sharedPool': 'Shared cost pool',
  'countries.sharedPoolDescription': 'Non-assignable products valued at quantity × price. The pool is distributed to countries by the selected key; the allocations add up to the pool.',
  'countries.sharedPoolUnpriced': '{count} products in the pool have no price – that much is missing from the allocation.',
  'countries.sharedPoolEmpty': 'No shared-cost products. Products can be flagged as shared cost under Administration → Products and prices.',
  'countries.sharedPoolUnallocated': 'The pool could not be distributed: the allocation key is 0 in every country.',
  'countries.sharePercent': '{percent} % share',
  'admin.prices.connector': 'License source',
  'admin.prices.connector.all': 'All license sources',
  'admin.prices.connectorHint': 'Prices are kept per license source: the same product can have a different price in another tenant.',
  'admin.prices.table.costItems': 'Procurement',
  'admin.prices.derived': 'derived',
  'admin.prices.derivedHint': 'The price is derived from the cost positions (quantity-weighted) and therefore not edited directly.',
  'admin.prices.uncovered': '{units} units without a position',
  'admin.prices.mixedCurrencies': 'Mixed currencies – no common price.',
  'admin.prices.costItems': 'Cost positions',
  'admin.prices.costItems.open': 'Procurement',
  'admin.prices.costItems.description': 'One entry per procurement route – for example Microsoft EA via Bechtle, Vodafone or direct purchase. The effective price is the quantity-weighted average.',
  'admin.prices.costItems.none': 'No position yet. Without positions the price is maintained directly on the product.',
  'admin.prices.costItems.add': 'Add position',
  'admin.prices.costItems.source': 'Procurement source',
  'admin.prices.costItems.contractRef': 'Contract / order number',
  'admin.prices.costItems.units': 'Quantity',
  'admin.prices.costItems.unitsRest': 'remaining',
  'admin.prices.costItems.unitsHint': 'Empty means "all remaining units" – possible only once.',
  'admin.prices.costItems.unitCost': 'Price per unit / month',
  'admin.prices.costItems.currency': 'Currency',
  'admin.prices.costItems.validFrom': 'Valid from',
  'admin.prices.costItems.validTo': 'Valid to',
  'admin.prices.costItems.note': 'Note',
  'admin.prices.costItems.expired': 'not active',
  'admin.prices.costItems.contracted': 'Contracted cost per month',
  'admin.prices.costItems.effective': 'Effective price',
  'admin.prices.costItems.saved': 'Cost position saved.',
  'admin.prices.costItems.deleted': 'Cost position removed.',
  'admin.prices.costItems.confirmDelete': 'Remove this cost position?',

  'people.title': 'Cost per person',
  'people.description': 'All licences and assigned devices of a person across every source, merged by e-mail address. Licence costs are monthly, the hardware figure is the one-off purchase price – the two are deliberately not added up.',
  'people.searchPlaceholder': 'Name, e-mail, sign-in name or department',
  'people.onlyPriced': 'Only with known costs',
  'people.table.name': 'Person',
  'people.table.country': 'Country',
  'people.table.department': 'Department',
  'people.table.sources': 'Sources',
  'people.table.licenses': 'Licenses',
  'people.table.cost': 'Cost/month',
  'people.unpriced': '{count} without price',
  'people.totals': '{count} people · {cost} per month (known prices)',
  'people.totalsUnpriced': '{count} people with unpriced licenses',
  'people.disabled': 'Account disabled',
  'people.table.devices': 'Devices',
  'people.table.hardware': 'Hardware (purchase)',
  'people.showSoftware': 'Show software costs',
  'people.showHardware': 'Show hardware costs',
  'people.unpricedDevices': '{count} without price',
  'people.totalsHardware': '{cost} purchase value (one-off, {count} devices)',
  'people.totalsPeople': '{count} people',
  'people.hardwareHint': 'One-off purchase value – not monthly.',

  'role.LicenseAdmin': 'Administration',
  'role.LicenseOperator': 'Editing',
  'role.LicenseReader': 'Read-only',
  'role.LicenseIngest': 'Data delivery (system)',
  'role.CatalogReader': 'IT catalogue (read-only)',

  'error.network': 'The server cannot be reached. Please try again later.',
  'error.unauthorized': 'Your session has expired. Please sign in again.',
  'error.forbidden': 'Your role does not permit this action.',
  'error.conflict': 'The action conflicts with the current state.',
  'error.unexpected': 'An unexpected error has occurred.',
  'error.correlationId': 'Support reference number',
  'error.correlationIdLocal': 'Local reference no. (the server did not report one)',
  'nav.catalog': 'IT catalogue',
  'nav.opensInNewTab': 'Opens in a new tab',
  'catalog.toLicenseManagement': 'Go to licence management',
  'catalog.hardware': 'Hardware',
  'catalog.software': 'Software',
  'catalog.title': 'IT catalogue',
  'catalog.description': 'Hardware and software supported by IT. Anything listed here may be ordered.',
  'catalog.search': 'Search by product, manufacturer or model',
  'catalog.filter.category': 'Category',
  'catalog.filter.manufacturer': 'Manufacturer',
  'catalog.filter.support': 'Support',
  'catalog.filter.all': 'All',
  'catalog.filter.reset': 'Reset filters',
  'catalog.empty': 'No entries match this selection.',
  'catalog.emptyHint': 'Choose another category or reset the search.',
  'catalog.count.one': '1 entry',
  'catalog.count.other': '{count} entries',
  'catalog.details': 'Details',
  'catalog.order': 'Order',
  'catalog.orderNote': 'Ordering information',
  'catalog.price': 'Price',
  'catalog.priceHint': 'Net list price for ordering. Leave empty if unknown – empty does not mean free.',
  'catalog.priceInterval': 'Billing',
  'catalog.priceInterval.ONE_TIME': 'one-off',
  'catalog.priceInterval.MONTHLY': 'per month',
  'catalog.priceInterval.YEARLY': 'per year',
  'catalog.priceUnknown': 'Price on request',
  'catalog.specifications': 'Specifications',
  'catalog.operatingSystems': 'Operating systems',
  'catalog.intendedUse': 'Intended use',
  'catalog.audience': 'Who may order',
  'catalog.manufacturer': 'Manufacturer',
  'catalog.model': 'Model / part number',
  'catalog.modelSoftware': 'Version / edition',
  'catalog.updatedAt': 'Last changed on {date}',
  'catalog.support.STANDARD': 'Standard',
  'catalog.support.SUPPORTED': 'Supported',
  'catalog.support.LIMITED': 'Limited support',
  'catalog.support.PHASE_OUT': 'Being phased out',
  'catalog.support.NOT_ORDERABLE': 'No longer orderable',
  'catalog.publish.DRAFT': 'Draft',
  'catalog.publish.PUBLISHED': 'Published',
  'catalog.publish.ARCHIVED': 'Archived',
  'catalog.notOrderableHint': 'This product can no longer be ordered. It remains listed so existing devices stay identifiable.',
  'catalog.limitedHint': 'Limited support – please check with IT before ordering.',
  'catalog.admin.title': 'Manage catalogue',
  'catalog.admin.description': 'Create, edit, publish and archive entries.',
  'catalog.admin.categories': 'Categories',
  'catalog.admin.categoriesDescription': 'Catalogue categories – separate categories per branch, order freely adjustable.',
  'catalog.admin.newItem': 'Add entry',
  'catalog.admin.newCategory': 'Add category',
  'catalog.admin.editItem': 'Edit entry',
  'catalog.admin.editCategory': 'Edit category',
  'catalog.admin.preview': 'Preview',
  'catalog.admin.previewHint': 'This is how employees will see the entry once published.',
  'catalog.admin.saveDraft': 'Save as draft',
  'catalog.admin.publish': 'Publish',
  'catalog.admin.archive': 'Archive',
  'catalog.admin.unarchive': 'Publish again',
  'catalog.admin.deleteForever': 'Delete permanently',
  'catalog.admin.deleteCategoryConfirm': 'Delete category “{name}” permanently? Only possible while it holds no entries.',
  'catalog.admin.archiveConfirm': 'Archive “{name}”? The entry leaves the catalogue but is kept.',
  'catalog.admin.saved': 'Saved.',
  'catalog.admin.filterStatus': 'Status',
  'catalog.admin.image': 'Image',
  'catalog.admin.imageUpload': 'Choose image',
  'catalog.admin.imageRemove': 'Remove image',
  'catalog.admin.imageHint': 'PNG, JPEG or WebP, 2 MB at most. SVG is not permitted for security reasons.',
  'catalog.admin.imageTooLarge': 'The image is too large. 2 MB at most.',
  'catalog.admin.german': 'German',
  'catalog.admin.english': 'English',
  'catalog.admin.englishHint': 'Optional. If left empty, the English catalogue shows the German text.',
  'catalog.admin.missingEnglish': 'English version incomplete',
  'catalog.admin.kind': 'Branch',
  'catalog.admin.name': 'Product name',
  'catalog.admin.shortDescription': 'Short description',
  'catalog.admin.longDescription': 'Full description',
  'catalog.admin.orderUrl': 'Link to the ordering process',
  'catalog.admin.orderUrlHint': 'Only http, https or mailto.',
  'catalog.admin.orderFieldEnabled': 'Ask for an additional detail when ordering',
  'catalog.admin.orderFieldEnabledHint': 'For generic items that need a detail before they can be ordered – e.g. the device model for a screen protector. The field appears in the cart, the value goes into the ticket.',
  'catalog.admin.orderFieldLabel': 'Field label',
  'catalog.admin.orderFieldLabelPlaceholder': 'e.g. Device model',
  'catalog.admin.orderFieldLabelMissing': 'Without a label nobody knows what to enter.',
  'catalog.admin.orderFieldHint': 'Hint below the field',
  'catalog.admin.orderFieldHintPlaceholder': 'e.g. iPhone 15 Pro',
  'catalog.admin.orderFieldRequired': 'Required – the request cannot be sent without it',
  'catalog.admin.sortOrder': 'Order',
  'catalog.admin.sortOrderHint': 'Lower numbers first; alphabetical when equal.',
  'catalog.admin.active': 'Active',
  'catalog.admin.slug': 'Key',
  'catalog.admin.slugHint': 'Leave empty – derived from the name.',
  'catalog.admin.slugPreview': 'Stored as “{slug}”. Capitalisation and special characters are normalised.',
  'catalog.admin.specLabel': 'Label',
  'catalog.admin.specValue': 'Value',
  'catalog.admin.specAdd': 'Add specification',
  'catalog.admin.specEmpty': 'No specifications recorded yet.',
  'catalog.admin.osAdd': 'Add operating system',
  'catalog.admin.itemCount': 'Entries',
  'catalog.admin.requiredMissing': 'Please fill in product name, manufacturer and category.',
  'nav.assets': 'Devices',
  'nav.myDevices': 'My devices',
  'nav.mySoftware': 'My software',
  'licenses.my.title': 'My software',
  'licenses.my.description': 'The licences assigned to your accounts – across all licence sources.',
  'licenses.my.empty': 'No licence is currently assigned to you.',
  'licenses.my.emptyHint': 'That does not necessarily mean you have none – your account may be listed here under a different address.',
  'licenses.my.emptySearchedFor': 'Searched for {address}.',
  'licenses.my.assignedAt': 'Assigned since',
  'licenses.my.account': 'Account',
  'licenses.my.sourceHint': 'Assignments come from the connected licence sources and are synchronised regularly.',
  'catalog.priceIntervalSoftwareHint': 'Software is always listed at a yearly price in the catalogue.',
  'assets.status.IN_USE': 'In use',
  'assets.status.IN_STOCK': 'In stock',
  'assets.status.RESERVED': 'Reserved',
  'assets.status.IN_REPAIR': 'Under repair',
  'assets.status.RETIRED': 'Retired',
  'assets.status.LOST': 'Lost or stolen',
  'assets.field.name': 'Device',
  'assets.field.assetTag': 'Asset tag',
  'assets.field.serialNumber': 'Serial number',
  'assets.field.deviceType': 'Device type',
  'assets.field.status': 'Status',
  'assets.field.holder': 'Assigned to',
  'assets.field.location': 'Location',
  'assets.field.costCenter': 'Cost centre',
  'assets.field.changedBy': 'Changed by',
  'assets.field.importedBy': 'Import from {source}',
  'assets.field.notes': 'Notes',
  'assets.field.warrantyEnd': 'Warranty until',
  'assets.field.lastSeen': 'Last reported',
  'assets.field.purchaseDate': 'Purchase date',
  'assets.field.purchaseCost': 'Purchase cost',
  'assets.noPrice': 'no price on record',
  'assets.warrantyExpired': 'The warranty has expired.',
  'assets.my.title': 'My devices',
  'assets.my.description': 'The devices assigned to you, with serial and asset numbers for when IT asks for them.',
  'assets.my.empty': 'No device is currently assigned to you.',
  'assets.my.emptyHint': 'That does not necessarily mean you have none – the assignment may simply be missing in the source system.',
  'assets.my.emptySearchedFor': 'Searched for {address} – if the device is booked to a different address in the source system, the link is missing.',
  'assets.my.sourceHint': 'The inventory comes from IT device management and is updated regularly.',
  'assets.admin.title': 'Device management',
  'assets.admin.description': 'The full device inventory. What is maintained here is what no source system delivers – assignment, location and notes.',
  'assets.admin.count.one': '1 device',
  'assets.admin.count.other': '{count} devices',
  'assets.admin.search': 'Search',
  'assets.admin.searchPlaceholder': 'Device, serial number, asset tag or person',
  'assets.admin.empty': 'No devices match this selection.',
  'assets.admin.emptyHint': 'Reset the filters – or no inventory has been delivered yet.',
  'assets.admin.kpi.total': 'Devices in total',
  'assets.admin.kpi.unresolved': 'Unassigned',
  'assets.admin.kpi.unresolvedHint': 'A holder was reported but could not be matched to a person.',
  'assets.admin.unresolved': 'not matched',
  'assets.admin.noHolder': 'assigned to nobody',
  'assets.admin.history': 'History',
  'assets.admin.noHistory': 'No history recorded for this device yet.',
  'assets.admin.unknownActor': 'not recorded',
  'assets.admin.ongoing': 'ongoing',
  'assets.admin.saved': 'Saved.',
  'assets.field.collection': 'Collection',
  'assets.field.collectionGroup': 'Group',
  'assets.field.composition': 'Composition',
  'assets.field.deviceCount': 'Devices',
  'assets.admin.tab.devices': 'Devices',
  'assets.admin.tab.collections': 'Collections',
  'assets.admin.includeCollections': 'Include devices from collections',
  'assets.admin.includeCollectionsHint': 'Desks and rooms live on the “Collections” tab. Enable this only to look up one of their devices individually.',
  'assets.admin.inCollection': 'in collection',
  'assets.admin.kpi.collections': 'Collections',
  'assets.admin.kpi.inCollections': 'Devices in collections',
  'assets.admin.kpi.inCollectionsHint': 'Desks, meeting rooms and other collections from the source system.',
  'assets.admin.collections.description': 'Desks, meeting rooms and other collections from the source system – one row per collection with everything that belongs to it.',
  'assets.admin.collections.count.one': '1 collection',
  'assets.admin.collections.count.other': '{count} collections',
  'assets.admin.collections.empty': 'There are no collections yet.',
  'assets.admin.collections.emptyHint': 'Collections arrive with the delivery from the source system once the view provides the “collection” column.',
  'assets.admin.collections.search': 'Find collection',
  'assets.admin.collections.searchPlaceholder': 'Tisch-0001, Raum Amsel, location, person …',
  'assets.admin.collections.lentTo': 'Lent to',
  'assets.admin.collections.notLent': 'not lent out',
  'assets.admin.collections.show': 'Show devices',
  'assets.admin.collections.hide': 'Hide devices',
  'assets.admin.collections.loading': 'Loading devices …',
  'assets.admin.collections.holderHint': 'pseudo user from the source system',
  'scan.button': 'Scan',
  'scan.title': 'Scan barcode',
  'scan.starting': 'Starting camera …',
  'scan.hint': 'Hold the label inside the frame. The code is recognised automatically.',
  'scan.denied': 'Camera access was denied. Allow it in your browser settings – or take a photo instead.',
  'scan.noCamera': 'No camera is available on this device. You can take or choose a photo of the label instead.',
  'scan.failed': 'The camera could not be started. Try taking a photo.',
  'scan.nothingFound': 'No barcode was recognised in the picture. Move closer, hold it straight, use good light.',
  'scan.timeout': 'The camera did not deliver a picture. Check the site’s camera permission – or take a photo instead.',
  'scan.tapToStart': 'Start camera',
  'scan.takePhoto': 'Take photo',
  'scan.result': 'Scanned: {code}',
  'assets.admin.sourceFields': 'From source “{source}” (id {externalId}), last reported on {lastSeen}. Source fields are overwritten by the next delivery.',
};
